/**
 * Sample data for the design preview screens.
 *
 * Hardcoded — no store, no tRPC, no auth. The shape mirrors the
 * production `Trip` / `Day` / `Place` types loosely but is its own
 * thing so the mocks can evolve independently of production schemas
 * during the design-iteration phase.
 *
 * Hero photo URLs use Unsplash; if any fail to load the screens fall
 * back to a flat accent-color block.
 */

import { unsplashUrl } from './unsplash';

import type { CardTag } from '@my-fullstack-app/itinerary-types';

export interface DesignPlace {
  id: string;
  name: string;
  category: string;
  startTime: string;
  endTime: string;
  address: string;
  notes?: string | undefined;
  hero: string;
  /** Google Places photo attribution — must be displayed alongside any
   *  rendered place photo per Google Maps Platform ToS §3.2.3. Carried
   *  through from the real `Place.photoAttribution` by `adaptPlace`. */
  photoAttribution?: { displayName: string; uri?: string | undefined } | undefined;
  tags?: TagId[] | undefined;
  /** Real-data semantic tag (important / reach-goal / missing-info), keyed
   *  by place.id in the canonical tagsMap. Distinct from the design-data
   *  category tags above (food/hotel/etc.) which are mock-only. */
  cardTag?: CardTag | undefined;
  /** Approximate position on the Discover map (top%, left%). Mock-only. */
  pin?: { top: number; left: number } | undefined;
  /** Transport from the previous place to here. The first place in a day has no connector. Mock-only. */
  connector?: DesignConnector | undefined;
  /** Approximate distance shown on add-place suggestions. Mock-only. */
  distanceKm?: number | undefined;
}

export type DesignTransportMode = 'walk' | 'car' | 'metro' | 'train' | 'bus' | 'bike' | 'taxi' | 'ferry' | 'flight';

export interface DesignConnector {
  mode: DesignTransportMode;
  /** Duration in minutes. */
  durationMin: number;
}

export type TagId = 'food' | 'hotel' | 'outdoors' | 'nightlife' | 'sightseeing' | 'beach' | 'transport';

export const TAG_CONFIG: Record<TagId, { label: string; bg: string; ink: string }> = {
  food: { label: 'Food', bg: '#FFE9DD', ink: '#A03C00' },
  hotel: { label: 'Hotel', bg: '#E8E4FF', ink: '#3D2FB5' },
  outdoors: { label: 'Outdoors', bg: '#E1F4DD', ink: '#235A20' },
  nightlife: { label: 'Nightlife', bg: '#F0E1F8', ink: '#5C2484' },
  sightseeing: { label: 'Sights', bg: '#E0F1F8', ink: '#1A4D69' },
  beach: { label: 'Beach', bg: '#FFF4D6', ink: '#73510A' },
  transport: { label: 'Transit', bg: '#EAEAEA', ink: '#444' },
};

export type ContributorRole = 'owner' | 'editor' | 'viewer';

export interface DesignContributor {
  id: string;
  name: string;
  role: ContributorRole;
  avatar?: string | undefined;
  /** When set, shown as a "Pending" invite instead of an active contributor. */
  pending?: boolean | undefined;
}

export interface DesignDay {
  id: string;
  label: string;
  date: string;
  subtitle: string;
  hero: string;
  places: DesignPlace[];
}

export interface DesignTrip {
  id: string;
  name: string;
  destination: string;
  country: string;
  rating: number;
  reviews: number;
  startDate: string;
  endDate: string;
  duration: string;
  blurb: string;
  hero: string;
  days: DesignDay[];
  contributors: DesignContributor[];
  /** Background image used by the Discover-tab map view (aerial / satellite). */
  mapBackdrop: string;
  /** Public share-link slug shown on the Share screen. */
  shareSlug: string;
}

const RIO_HERO = unsplashUrl('photo-1483729558449-99ef09a8c325', { w: 1000 });
const TOKYO_HERO = unsplashUrl('photo-1493780474015-ba834fd0ce2f', { w: 1000 });
const NORWAY_HERO = unsplashUrl('photo-1488646953014-85cb44e25828', { w: 1000 });
const MARRAKECH_HERO = unsplashUrl('photo-1597212618440-806262de4f6b', { w: 1000 });

// Aerial / satellite-style backdrops for the Discover map view. Real
// map SDK is out of scope for the mock — these stand in for what the
// production map would render.
const RIO_AERIAL = unsplashUrl('photo-1518803194621-27188ba362c9', { w: 1200 });
const TOKYO_AERIAL = unsplashUrl('photo-1542051841857-5f90071e7989', { w: 1200 });
const NORWAY_AERIAL = unsplashUrl('photo-1531366936337-7c912a4589a7', { w: 1200 });
const MARRAKECH_AERIAL = unsplashUrl('photo-1539020140153-e479b8c22e70', { w: 1200 });

const HOTEL_PHOTO = unsplashUrl('photo-1566073771259-6a8506099945', { w: 800 });
const BEACH_PHOTO = unsplashUrl('photo-1507525428034-b723cf961d3e', { w: 800 });
const FOOD_PHOTO = unsplashUrl('photo-1517248135467-4c7edcad34c4', { w: 800 });
const AIRPORT_PHOTO = unsplashUrl('photo-1436491865332-7a61a109cc05', { w: 800 });
const SUGARLOAF_PHOTO = unsplashUrl('photo-1518803194621-27188ba362c9', { w: 800 });
const SAMBA_PHOTO = unsplashUrl('photo-1518733057094-95b53143d2a7', { w: 800 });
const FALLS_PHOTO = unsplashUrl('photo-1467811884478-9a217d12c2bd', { w: 800 });

// Avatar URLs (Unsplash portraits — stable, free).
const A_VANESSA = unsplashUrl('photo-1494790108377-be9c29b29330', { w: 160 });
const A_MARCO = unsplashUrl('photo-1507003211169-0a1dd7228f2d', { w: 160 });
const A_PRIYA = unsplashUrl('photo-1438761681033-6461ffad8d80', { w: 160 });
const A_DANIEL = unsplashUrl('photo-1500648767791-00dcc994a43e', { w: 160 });

export const DESIGN_TRIPS: DesignTrip[] = [
  {
    id: 'iconic-brazil',
    name: 'Rio de Janeiro',
    destination: 'Iconic Brazil',
    country: 'Brazil',
    rating: 5.0,
    reviews: 143,
    startDate: 'Wed, Oct 21',
    endDate: 'Sun, Nov 1',
    duration: '8 days',
    blurb:
      "Rio de Janeiro, often simply called Rio, is one of Brazil's most iconic cities, renowned for its beaches, Sugarloaf Mountain, and the Christ the Redeemer statue.",
    hero: RIO_HERO,
    mapBackdrop: RIO_AERIAL,
    shareSlug: 'kiips.app/t/rio-x7q9',
    contributors: [
      { id: 'c-vanessa', name: 'Vanessa Lima', role: 'owner', avatar: A_VANESSA },
      { id: 'c-marco', name: 'Marco Silva', role: 'editor', avatar: A_MARCO },
      { id: 'c-priya', name: 'Priya Patel', role: 'viewer', avatar: A_PRIYA },
      { id: 'c-daniel', name: 'daniel@example.com', role: 'editor', pending: true },
    ],
    days: [
      {
        id: 'rio-d1',
        label: 'Day 1',
        date: 'Wed, Oct 21',
        subtitle: 'Arrival to Rio de Janeiro',
        hero: AIRPORT_PHOTO,
        places: [
          {
            id: 'rio-d1-p1',
            name: 'Galeão International Airport',
            category: 'Arrival',
            startTime: '08:30',
            endTime: '09:30',
            address: 'Av. Vinte de Janeiro, Rio de Janeiro',
            hero: AIRPORT_PHOTO,
            tags: ['transport'],
            pin: { top: 18, left: 64 },
          },
          {
            id: 'rio-d1-p2',
            name: 'Hotel Fasano Rio',
            category: 'Hotel · Check-in',
            startTime: '10:30',
            endTime: '11:30',
            address: 'Av. Vieira Souto 80, Ipanema',
            notes: [
              '- [x] Confirm reservation',
              '- [x] Save check-in QR',
              '- [ ] Print backup copy',
              'Welcome drink at the rooftop bar #drink #hotel',
            ].join('\n'),
            hero: HOTEL_PHOTO,
            tags: ['hotel'],
            connector: { mode: 'car', durationMin: 50 },
            pin: { top: 56, left: 38 },
          },
          {
            id: 'rio-d1-p3',
            name: 'Praia de Ipanema',
            category: 'Beach walk',
            startTime: '15:00',
            endTime: '17:30',
            address: 'Av. Vieira Souto, Ipanema',
            notes: '#sunset is around 17:40 today — start at the south end and walk toward Posto 9.',
            hero: BEACH_PHOTO,
            tags: ['beach', 'outdoors'],
            connector: { mode: 'walk', durationMin: 8 },
            pin: { top: 62, left: 42 },
          },
          {
            id: 'rio-d1-p4',
            name: 'Aprazível',
            category: 'Welcome dinner',
            startTime: '20:00',
            endTime: '22:30',
            address: 'R. Aprazível 62, Santa Teresa',
            notes: [
              '- [ ] Confirm 8pm reservation',
              '- [x] Note dietary preferences',
              'Reservation under "Vanessa" — terrace seating requested. #dinner #reservation',
            ].join('\n'),
            hero: FOOD_PHOTO,
            tags: ['food', 'nightlife'],
            connector: { mode: 'car', durationMin: 25 },
            pin: { top: 38, left: 24 },
          },
        ],
      },
      {
        id: 'rio-d2',
        label: 'Day 2',
        date: 'Thu, Oct 22',
        subtitle: 'Rio Highlights',
        hero: SUGARLOAF_PHOTO,
        places: [
          {
            id: 'rio-d2-p1',
            name: 'Sugarloaf Mountain',
            category: 'Cable car',
            startTime: '09:00',
            endTime: '11:30',
            address: 'Av. Pasteur 520, Urca',
            notes: [
              '- [x] Buy cable car tickets online',
              '- [ ] Bring camera',
              '- [ ] Charge phone',
              '#sightseeing #must-see — go early to beat the crowds.',
            ].join('\n'),
            hero: SUGARLOAF_PHOTO,
            tags: ['sightseeing', 'outdoors'],
            pin: { top: 32, left: 50 },
          },
          {
            id: 'rio-d2-p2',
            name: 'Confeitaria Colombo',
            category: 'Lunch',
            startTime: '12:30',
            endTime: '14:00',
            address: 'R. Gonçalves Dias 32, Centro',
            notes: 'Order the bolinhos de bacalhau and the empadinha. #lunch',
            hero: FOOD_PHOTO,
            tags: ['food'],
            connector: { mode: 'metro', durationMin: 18 },
            pin: { top: 28, left: 28 },
          },
          {
            id: 'rio-d2-p3',
            name: 'Christ the Redeemer',
            category: 'Sightseeing',
            startTime: '15:00',
            endTime: '17:30',
            address: 'Parque Nacional da Tijuca',
            notes: [
              '- [ ] Take the cog train (not the van)',
              '- [ ] Plan for ~20min queue at the top',
              '#sightseeing #views',
            ].join('\n'),
            hero: RIO_HERO,
            tags: ['sightseeing', 'outdoors'],
            connector: { mode: 'car', durationMin: 35 },
            pin: { top: 44, left: 18 },
          },
          {
            id: 'rio-d2-p4',
            name: 'Carioca da Gema',
            category: 'Live samba',
            startTime: '21:00',
            endTime: '23:30',
            address: 'Av. Mem de Sá 79, Lapa',
            hero: SAMBA_PHOTO,
            tags: ['nightlife'],
            connector: { mode: 'taxi', durationMin: 20 },
            pin: { top: 24, left: 36 },
          },
        ],
      },
      {
        id: 'rio-d3',
        label: 'Day 3',
        date: 'Fri, Oct 23',
        subtitle: 'Iguazu Falls',
        hero: FALLS_PHOTO,
        places: [
          {
            id: 'rio-d3-p1',
            name: 'Foz do Iguaçu (flight)',
            category: 'Travel',
            startTime: '07:00',
            endTime: '09:30',
            address: 'GIG → IGU, LATAM 4321',
            hero: AIRPORT_PHOTO,
            tags: ['transport'],
            pin: { top: 22, left: 8 },
          },
          {
            id: 'rio-d3-p2',
            name: 'Iguazu Falls — Brazilian side',
            category: 'Walking trail',
            startTime: '11:00',
            endTime: '15:00',
            address: 'Parque Nacional do Iguaçu',
            notes: [
              '- [ ] Wear waterproof shoes',
              '- [ ] Pack a poncho',
              '#waterfall #nature — the trail loops back to the visitor centre.',
            ].join('\n'),
            hero: FALLS_PHOTO,
            tags: ['outdoors', 'sightseeing'],
            connector: { mode: 'car', durationMin: 25 },
            pin: { top: 70, left: 8 },
          },
          {
            id: 'rio-d3-p3',
            name: 'Belmond Hotel das Cataratas',
            category: 'Hotel · Check-in',
            startTime: '16:00',
            endTime: '17:00',
            address: 'Rod. BR-469 Km 32',
            notes: [
              '- [x] Confirm reservation',
              '- [ ] Book sunset cocktails',
              'Inside the national park — early dinner. #hotel #sunset',
            ].join('\n'),
            hero: HOTEL_PHOTO,
            tags: ['hotel'],
            connector: { mode: 'car', durationMin: 12 },
            pin: { top: 78, left: 14 },
          },
        ],
      },
    ],
  },
  {
    id: 'tokyo-blossom',
    name: 'Tokyo',
    destination: 'Cherry Blossom',
    country: 'Japan',
    rating: 4.9,
    reviews: 218,
    startDate: 'Mon, Apr 1',
    endDate: 'Sun, Apr 7',
    duration: '7 days',
    blurb:
      'A week of cherry blossoms across Tokyo and Kyoto. Hanami picnics, hidden teahouses, and slow trains through the countryside.',
    hero: TOKYO_HERO,
    mapBackdrop: TOKYO_AERIAL,
    shareSlug: 'kiips.app/t/tokyo-blossom-9k',
    contributors: [
      { id: 'c-vanessa', name: 'Vanessa Lima', role: 'owner', avatar: A_VANESSA },
      { id: 'c-daniel', name: 'Daniel Park', role: 'editor', avatar: A_DANIEL },
    ],
    days: [],
  },
  {
    id: 'norwegian-fjords',
    name: 'Bergen',
    destination: 'Norwegian Fjords',
    country: 'Norway',
    rating: 4.8,
    reviews: 96,
    startDate: 'Sat, Jul 5',
    endDate: 'Wed, Jul 9',
    duration: '5 days',
    blurb:
      'Cruise the western fjords from Bergen up to Geiranger. Long-light evenings, glacier hikes, and wooden cabin nights.',
    hero: NORWAY_HERO,
    mapBackdrop: NORWAY_AERIAL,
    shareSlug: 'kiips.app/t/fjords-mp4',
    contributors: [
      { id: 'c-vanessa', name: 'Vanessa Lima', role: 'owner', avatar: A_VANESSA },
    ],
    days: [],
  },
  {
    id: 'marrakech-markets',
    name: 'Marrakech',
    destination: 'Souk & Riads',
    country: 'Morocco',
    rating: 4.7,
    reviews: 132,
    startDate: 'Fri, Mar 14',
    endDate: 'Mon, Mar 17',
    duration: '4 days',
    blurb:
      'Four days inside the medina. Riad courtyards, late-night souks, dawn calls to prayer over rooftop tea.',
    hero: MARRAKECH_HERO,
    mapBackdrop: MARRAKECH_AERIAL,
    shareSlug: 'kiips.app/t/medina-r2x',
    contributors: [
      { id: 'c-vanessa', name: 'Vanessa Lima', role: 'owner', avatar: A_VANESSA },
      { id: 'c-priya', name: 'Priya Patel', role: 'viewer', avatar: A_PRIYA },
    ],
    days: [],
  },
];


/**
 * Parse a notes markdown string for checkbox progress. Returns null if
 * the notes don't contain any checkbox lines. Used by `<PlaceIndicators>`
 * to render the small `2/4` progress chip on cards.
 */
export function checklistProgress(notes: string | undefined): { done: number; total: number } | null {
  if (!notes) return null;
  let done = 0;
  let total = 0;
  for (const line of notes.split('\n')) {
    const m = line.trim().match(/^- \[( |x)\] /i);
    if (m) {
      total++;
      if (m[1]!.toLowerCase() === 'x') done++;
    }
  }
  return total > 0 ? { done, total } : null;
}
